import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";

const Tooltip = ({ anchorRef, text, position = "bottom", delay = 300 }) => {
  const [coords, setCoords] = useState(null);
  const [adjustedPosition, setAdjustedPosition] = useState(null);
  const timeoutRef = useRef(null);
  const tooltipRef = useRef(null);
  const isHoveredRef = useRef(false);

  useEffect(() => {
    if (!anchorRef || !anchorRef.current) return;
    const anchorEl = anchorRef.current;

    const handleMouseEnter = () => {
      timeoutRef.current = setTimeout(() => {
        const rect = anchorEl.getBoundingClientRect();
        setCoords(rect);
      }, delay);
    };

    const handleMouseLeave = () => {
      clearTimeout(timeoutRef.current);
      // Only hide the tooltip if it's not being hovered
      setTimeout(() => {
        if (!isHoveredRef.current) {
          setCoords(null);
          setAdjustedPosition(null);
        }
      }, 50); // Small delay to allow hover detection on the tooltip
    };

    anchorEl.addEventListener("mouseenter", handleMouseEnter);
    anchorEl.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      clearTimeout(timeoutRef.current);
      anchorEl.removeEventListener("mouseenter", handleMouseEnter);
      anchorEl.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [anchorRef, delay]);

  // Second useEffect to adjust position after tooltip is rendered and we have actual dimensions
  useEffect(() => {
    if (!coords || !tooltipRef.current) return;

    const calculatePosition = () => {
      // Safety check in case ref becomes null before this executes
      if (!tooltipRef.current) return;
      
      const offset = 10;
      const padding = 10;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const tooltipWidth = tooltipRect.width;
      const tooltipHeight = tooltipRect.height;

      let finalPosition = position;
      let top, left, transform;

      // Initial positioning
      if (position === "top") {
        top = coords.top - tooltipHeight - offset;
        left = coords.left + coords.width / 2;
        transform = "translateX(-50%)";
        
        // Check if tooltip goes above viewport
        if (top < padding) {
          finalPosition = "bottom";
          top = coords.bottom + offset;
        }
      } else if (position === "bottom") {
        top = coords.bottom + offset;
        left = coords.left + coords.width / 2;
        transform = "translateX(-50%)";
        
        // Check if tooltip goes below viewport
        if (top + tooltipHeight > viewportHeight - padding) {
          finalPosition = "top";
          top = coords.top - tooltipHeight - offset;
        }
      } else if (position === "left") {
        top = coords.top + coords.height / 2;
        left = coords.left - tooltipWidth - offset;
        transform = "translateY(-50%)";
        
        // Check if tooltip goes beyond left edge
        if (left < padding) {
          finalPosition = "right";
          left = coords.right + offset;
        }
      } else if (position === "right") {
        top = coords.top + coords.height / 2;
        left = coords.right + offset;
        transform = "translateY(-50%)";
        
        // Check if tooltip goes beyond right edge
        if (left + tooltipWidth > viewportWidth - padding) {
          finalPosition = "left";
          left = coords.left - tooltipWidth - offset;
        }
      }

      // Final horizontal boundary checks for top/bottom positioned tooltips
      if (finalPosition === "top" || finalPosition === "bottom") {
        const halfWidth = tooltipWidth / 2;
        if (left - halfWidth < padding) {
          left = halfWidth + padding;
        } else if (left + halfWidth > viewportWidth - padding) {
          left = viewportWidth - halfWidth - padding;
        }
      }

      // Final vertical boundary checks for left/right positioned tooltips
      if (finalPosition === "left" || finalPosition === "right") {
        const halfHeight = tooltipHeight / 2;
        if (top - halfHeight < padding) {
          top = halfHeight + padding;
        } else if (top + halfHeight > viewportHeight - padding) {
          top = viewportHeight - halfHeight - padding;
        }
      }

      setAdjustedPosition({ top, left, transform });
    };

    // Small delay to ensure tooltip is fully rendered
    requestAnimationFrame(calculatePosition);
  }, [coords, position]);

  const handleTooltipMouseEnter = () => {
    isHoveredRef.current = true;
  };

  const handleTooltipMouseLeave = () => {
    isHoveredRef.current = false;
    setCoords(null);
    setAdjustedPosition(null);
  };

  if (!coords) return null;

  // Use adjusted position if available, otherwise use a default that will be quickly replaced
  const style = adjustedPosition ? {
    top: adjustedPosition.top,
    left: adjustedPosition.left,
    transform: adjustedPosition.transform,
    width: "max-content",
    maxWidth: 200,
    whiteSpace: "normal",
    opacity: adjustedPosition ? 1 : 0 // Hide until position is calculated
  } : {
    top: -9999, // Off-screen until calculated
    left: -9999,
    width: "max-content",
    maxWidth: 200,
    whiteSpace: "normal",
    opacity: 0
  };

  return ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      className={`absolute z-1001 px-3 py-2 tw-paragraph-reg bg-white border border-[var(--color-gray-100)] text-[var(--color-gray-800)] rounded transition-opacity duration-200`}
      style={style}
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
    >
      {text}
    </div>,
    document.body
  );
};

export default React.memo(Tooltip);